/**
 * Menu Configuration - Defines sidebar menu items and permissions
 *
 * Matches Xamarin FlyoutMenuPage menu items.
 * Menu items are filtered based on user role and permissions.
 */

import {
  LayoutDashboard,
  ClipboardCheck,
  ClipboardList,
  Truck,
  Bell,
  Map,
  DoorOpen,
  Container,
  BarChart3,
  ArrowLeftRight,
  MessageSquare,
  BookOpen,
} from 'lucide-react';

import type { LucideIcon } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// App Configuration
// ─────────────────────────────────────────────────────────────────

export const APP_CONFIG = {
  name: 'YMS',
  fullName: 'Yard Jockey Management System',
  version: '1.0.576',
  description: 'Enterprise Yard Jockey Management System PWA',
} as const;

export const STORAGE_KEYS = {
  authToken: 'yms_access_token',
  refreshToken: 'yms_refresh_token',
  user: 'yms_user',
  plant: 'yms_plant',
  sessionId: 'yms_session_id',
  globalConfig: 'yms_global_config',
  otpMobile: 'yms_otp_mobile',
  otpCountryCode: 'yms_otp_country_code',
  theme: 'yms_theme',
  language: 'yms_language',
  // Offline support — matches Xamarin AppConstants offline keys
  offlineUpdatedTaskList: 'yms_offline_updated_task_list',
  offlineMyTaskList: 'yms_offline_my_task_list',
  inProgressTask: 'yms_in_progress_task',
  offlineUpcomingTask: 'yms_offline_upcoming_task',
  offlineCompletedTask: 'yms_offline_completed_task',
  breakModeActive: 'yms_break_mode_active',
  breakModeFrom: 'yms_break_mode_from',
  shuttleModeActive: 'yms_shuttle_mode_active',
  shuttleTimerStart: 'yms_shuttle_timer_start',
  shuttleTimerPausedElapsed: 'yms_shuttle_timer_paused_elapsed',
  breakTimerStart: 'yms_break_timer_start',
  tokenExpiry: 'yms_token_expiry',
  loginDateTime: 'yms_login_date_time',
  installId: 'yms_install_id',
  lastActivityTime: 'yms_last_activity_time',
} as const;

export const ROUTES = {
  home: '/',
  login: '/login',
  otp: '/otp',
  signup: '/signup',
  plantSelection: '/plant-selection',
  changePlant: '/change-plant', // Plant selection from sidebar (with header)
  dashboard: '/dashboard',
  // Feature routes
  createAuditTask: '/create-audit-task',
  createMoveTask: '/create-move-task',
  auditSummary: '/audit-summary',
  notifications: '/notifications',
  graphicalYard: '/graphical-yard',
  dockDoors: '/dock-doors',
  trailers: '/trailers',
  ymsCapacityDashboard: '/yms-capacity-dashboard',
  ymsDashboard: '/yms-dashboard',
  displacementSummary: '/displacement-summary',
  feedback: '/feedback',
  appGuide: '/app-guide',
  workModeSelection: '/work-mode-selection',
  shuttleTransfer: '/shuttle-transfer',
  breakMode: '/break-mode',
  logoutSummary: '/logout-summary',
  // Utility routes
  restrictedArea: '/restricted-area',
  maintenance: '/maintenance',
  error: '/error',
} as const;

export const PRIORITY_CONFIG = {
  CRITICAL: { min: 1, max: 10, name: 'Critical' as const, color: '#BE0000', lightBg: '#FFEDED' },
  HIGH: { min: 11, max: 29, name: 'High' as const, color: '#E38C25', lightBg: '#FFFCE4' },
  STANDARD: { min: 30, max: 99, name: 'Standard' as const, color: '#3B82F6', lightBg: '#E7EDF4' },
} as const;

// ─────────────────────────────────────────────────────────────────
// Menu Types
// ─────────────────────────────────────────────────────────────────

export interface MenuItem {
  id: string;
  label: string;
  labelKey: string; // i18n key
  icon: LucideIcon; // Fallback Lucide icon
  iconPath: string; // Path to normal (blue) SVG icon
  iconPathWhite: string; // Path to white SVG icon (for active state)
  route: string;
  roles: string[]; // Roles that can see this item
  badge?: string; // Optional badge (e.g., count)
  isExternal?: boolean; // Opens external URL in new tab instead of navigating
}

export interface MenuSection {
  id: string;
  title?: string;
  titleKey?: string; // i18n key for section title
  items: MenuItem[];
}

// ─────────────────────────────────────────────────────────────────
// User Roles
// ─────────────────────────────────────────────────────────────────

export const USER_ROLES = {
  YARD_JOCKEY: 'yard_jockey',
  SUPERVISOR: 'supervisor',
  ADMIN: 'admin',
  VIEWER: 'viewer',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// All roles for items visible to everyone
const ALL_ROLES = Object.values(USER_ROLES);

// Icon base path
const ICON_PATH = '/images/sidebar';

// ─────────────────────────────────────────────────────────────────
// Menu Configuration - Matches Xamarin FlyoutMenuList
// ─────────────────────────────────────────────────────────────────

export const MENU_SECTIONS: MenuSection[] = [
  {
    id: 'main',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        labelKey: 'navigation.dashboard',
        icon: LayoutDashboard,
        iconPath: `${ICON_PATH}/task.svg`,
        iconPathWhite: `${ICON_PATH}/task_white.svg`,
        route: ROUTES.dashboard,
        roles: ALL_ROLES,
      },
      {
        id: 'create-audit-task',
        label: 'Create Audit Task',
        labelKey: 'navigation.createAuditTask',
        icon: ClipboardCheck,
        iconPath: `${ICON_PATH}/create_audit.svg`,
        iconPathWhite: `${ICON_PATH}/create_audit_white.svg`,
        route: ROUTES.createAuditTask,
        roles: ALL_ROLES,
      },
      {
        id: 'create-move-task',
        label: 'Create Move Task',
        labelKey: 'navigation.createMoveTask',
        icon: Truck,
        iconPath: `${ICON_PATH}/create_move_task.svg`,
        iconPathWhite: `${ICON_PATH}/create_move_task_white.svg`,
        route: ROUTES.createMoveTask,
        roles: ALL_ROLES,
      },
      {
        id: 'notification',
        label: 'Notifications',
        labelKey: 'navigation.notification',
        icon: Bell,
        iconPath: `${ICON_PATH}/notification.svg`,
        iconPathWhite: `${ICON_PATH}/notification_white.svg`,
        route: ROUTES.notifications,
        roles: ALL_ROLES,
      },
      {
        id: 'graphical-yard',
        label: 'Graphical Yard',
        labelKey: 'navigation.graphicalYard',
        icon: Map,
        iconPath: `${ICON_PATH}/graphical_yard.svg`,
        iconPathWhite: `${ICON_PATH}/graphical_yard_white.svg`,
        route: ROUTES.graphicalYard,
        roles: ALL_ROLES,
        isExternal: true,
      },
      {
        id: 'dock-doors',
        label: 'Dock Doors',
        labelKey: 'navigation.dockDoors',
        icon: DoorOpen,
        iconPath: `${ICON_PATH}/dockdoors_icon.svg`,
        iconPathWhite: `${ICON_PATH}/dockdoors_icon_white.svg`,
        route: ROUTES.dockDoors,
        roles: ALL_ROLES,
      },
      {
        id: 'trailers',
        label: 'Trailers',
        labelKey: 'navigation.trailers',
        icon: Container,
        iconPath: `${ICON_PATH}/trailers.svg`,
        iconPathWhite: `${ICON_PATH}/trailers_white.svg`,
        route: ROUTES.trailers,
        roles: ALL_ROLES,
      },
      {
        id: 'yms-capacity-dashboard',
        label: 'Yard Capacity Dashboard',
        labelKey: 'navigation.ymsCapacityDashboard',
        icon: BarChart3,
        iconPath: `${ICON_PATH}/yms_capacity_dashboard.svg`,
        iconPathWhite: `${ICON_PATH}/yms_capacity_dashboard_white.svg`,
        route: ROUTES.ymsCapacityDashboard,
        roles: ALL_ROLES,
      },
      {
        id: 'yms-dashboard',
        label: 'YMS Dashboard',
        labelKey: 'navigation.ymsDashboard',
        icon: LayoutDashboard,
        iconPath: `${ICON_PATH}/yms_dashboard.svg`,
        iconPathWhite: `${ICON_PATH}/yms_dashboard_white.svg`,
        route: ROUTES.ymsDashboard,
        roles: ALL_ROLES,
      },
      {
        id: 'audit-summary',
        label: 'Audit Summary',
        labelKey: 'navigation.auditSummary',
        icon: ClipboardList,
        iconPath: `${ICON_PATH}/ic_audit_summary_blue.svg`,
        iconPathWhite: `${ICON_PATH}/ic_audit_summary_white.svg`,
        route: ROUTES.auditSummary,
        roles: ALL_ROLES,
        isExternal: true,
      },
      {
        id: 'displacement-summary',
        label: 'Displacement Summary',
        labelKey: 'navigation.displacementSummary',
        icon: ArrowLeftRight,
        iconPath: `${ICON_PATH}/ic_dispalce_summary.svg`,
        iconPathWhite: `${ICON_PATH}/ic_displace_summary_white.svg`,
        route: ROUTES.displacementSummary,
        roles: ALL_ROLES,
      },
      {
        id: 'feedback',
        label: 'Feedback',
        labelKey: 'navigation.feedback',
        icon: MessageSquare,
        iconPath: `${ICON_PATH}/feedback_menu.svg`,
        iconPathWhite: `${ICON_PATH}/feedback_menu_white.svg`,
        route: ROUTES.feedback,
        roles: ALL_ROLES,
      },
      {
        id: 'app-guide',
        label: 'App Guide',
        labelKey: 'navigation.appGuide',
        icon: BookOpen,
        iconPath: `${ICON_PATH}/app_guide_icon.svg`,
        iconPathWhite: `${ICON_PATH}/app_guide_icon_white.svg`,
        route: ROUTES.appGuide,
        roles: ALL_ROLES,
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
// Menu Helper Functions
// ─────────────────────────────────────────────────────────────────

/**
 * Filter menu items based on user roles
 */
export function filterMenuByRole(sections: MenuSection[], userRoles: string[]): MenuSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.roles.some((role) => userRoles.includes(role))),
    }))
    .filter((section) => section.items.length > 0);
}

/**
 * Get all visible menu items for a user
 */
export function getVisibleMenuItems(userRoles: string[]): MenuItem[] {
  return MENU_SECTIONS.flatMap((section) =>
    section.items.filter((item) => item.roles.some((role) => userRoles.includes(role)))
  );
}

/**
 * Get menu item by route
 */
export function getMenuItemByRoute(route: string): MenuItem | undefined {
  for (const section of MENU_SECTIONS) {
    const item = section.items.find((i) => i.route === route);
    if (item) return item;
  }
  return undefined;
}

export const Authentticatevalues = {
  UserStatusLogin: 'login',
  UserStatusLogout: 'logout',
  UserStatusAutoLogout: 'auto_logout',
  UserStatusStartYardWork: 'start_yard_work',
  UserStatusBreak: 'break',
  UserStatusContinue: 'continue',
  UserStatusEndYardWork: 'stop_yard_work',
  UserStatusStartShuttleTransfer: 'start_shuttle_transfer',
  UserStatusStopShuttleTransfer: 'stop_shuttle_transfer',
  UserStatusOnline: 'online',
  UserStatusOffline: 'offline',
};
